import { useQueryClient } from "@tanstack/react-query";

import { useUpsertJobUserSnapshot } from "@/hooks/mutations/useUpsertJobUserSnapshot";
import { dashboard } from "@/lib/dashboard";
import { CandidacyStatus } from "@/types";

// Resets the server-side applied baseline behind the dashboard's delta badge
// when a candidacy enters or leaves the applied bucket. The count is derived
// from whichever cache currently holds the job's candidacies, so this must run
// before the dashboard refetch replaces them.
export const useSetAppliedSnapshotForCandidacy = () => {
  const queryClient = useQueryClient();
  const { mutate: upsertJobUserSnapshot } = useUpsertJobUserSnapshot();

  return ({
    candidacyId,
    fromStatus,
    toStatus,
  }: {
    candidacyId: string;
    fromStatus?: CandidacyStatus;
    toStatus?: CandidacyStatus;
  }) => {
    if (fromStatus === undefined || toStatus === undefined) return;

    const context = dashboard.findCandidacyJobContext(queryClient, candidacyId);
    if (!context) return;

    const leftApplied =
      fromStatus === CandidacyStatus.APPLIED &&
      toStatus !== CandidacyStatus.APPLIED;
    // Undo puts the candidacy back in the bucket, so the baseline has to climb
    // again — otherwise the delta badge keeps counting a row that came back.
    const returnedToApplied =
      fromStatus !== CandidacyStatus.APPLIED &&
      toStatus === CandidacyStatus.APPLIED;
    if (!leftApplied && !returnedToApplied) return;

    const appliedCount = dashboard.countAppliedExcluding(
      context.candidacies,
      candidacyId,
    );

    upsertJobUserSnapshot({
      jobId: context.jobId,
      appliedCount: returnedToApplied ? appliedCount + 1 : appliedCount,
    });
  };
};
